import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Info, RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import {
  getWebsiteWidgetConfig,
  rotateWebsiteWidgetKey,
  updateWebsiteWidgetConfig,
} from "../../api/widgetIntegrationsApi";

const defaultState = {
  isActive: true,
  provider: "META_CLOUD",
  title: "Chat",
  subtitle: "Typically replies in 5 mins",
  welcomeText: "Hi! Welcome! How can we help?",
  chips: ["Pricing", "Book demo", "Help", "More"],
  requireName: true,
  requireEmailOrPhone: true,
  headerBgColor: "#14866d",
  buttonBgColor: "#25D366",
  allowedOrigins: [],
  whatsAppNumber: "",
  widgetKey: "",
};

const joinLines = values =>
  Array.isArray(values) ? values.filter(Boolean).join("\n") : "";

const parseLines = value =>
  String(value || "")
    .split(/\r?\n/)
    .map(v => v.trim())
    .filter(Boolean);

const parseChips = value =>
  String(value || "")
    .split(",")
    .map(v => v.trim())
    .filter(Boolean);

const normalizeProvider = value => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return normalized === "META" || normalized === "METACLOUD" || normalized === "META_CLOUD"
    ? "META_CLOUD"
    : "META_CLOUD";
};

const normalize = data => {
  const source = data || {};
  return {
    ...defaultState,
    ...source,
    provider: normalizeProvider(source.provider || defaultState.provider),
    chips: Array.isArray(source.chips) ? source.chips : defaultState.chips,
    allowedOrigins: Array.isArray(source.allowedOrigins)
      ? source.allowedOrigins
      : defaultState.allowedOrigins,
  };
};

export default function WebsiteWidgetSection() {
  const [state, setState] = useState(defaultState);
  const [chipsText, setChipsText] = useState(defaultState.chips.join(", "));
  const [allowedOriginsText, setAllowedOriginsText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWebsiteWidgetConfig();
      const next = normalize(data);
      setState(next);
      setChipsText(next.chips.join(", "));
      setAllowedOriginsText(joinLines(next.allowedOrigins));
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load widget config"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const embedSnippet = useMemo(() => {
    if (!state.widgetKey) return "";
    return [
      "<script>",
      `  window.XploreByteWidget = { key: "${state.widgetKey}" };`,
      "</script>",
      '<script async src="https://app.xplorebyte.com/widget.js"></script>',
    ].join("\n");
  }, [state.widgetKey]);

  const onChange = key => event => {
    const value =
      event?.target?.type === "checkbox"
        ? !!event.target.checked
        : event?.target?.value;
    setState(prev => ({ ...prev, [key]: value }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        isActive: !!state.isActive,
        title: state.title,
        subtitle: state.subtitle,
        welcomeText: state.welcomeText,
        chips: parseChips(chipsText),
        requireName: !!state.requireName,
        requireEmailOrPhone: !!state.requireEmailOrPhone,
        headerBgColor: state.headerBgColor,
        buttonBgColor: state.buttonBgColor,
        allowedOrigins: parseLines(allowedOriginsText),
        whatsAppNumber: state.whatsAppNumber,
      };

      const data = await updateWebsiteWidgetConfig(payload);
      const next = normalize(data);
      setState(next);
      setChipsText(next.chips.join(", "));
      setAllowedOriginsText(joinLines(next.allowedOrigins));
      toast.success("Website widget config updated");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update widget");
    } finally {
      setSaving(false);
    }
  };

  const onRotateKey = async () => {
    if (!window.confirm("Rotate public widget key? Existing embeds will stop working.")) {
      return;
    }

    setRotating(true);
    try {
      await rotateWebsiteWidgetKey();
      toast.success("Widget key rotated");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to rotate key");
    } finally {
      setRotating(false);
    }
  };

  const copyText = async value => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-800">
              Website Widget
            </h2>
            <div className="relative group">
              <button
                type="button"
                className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-50"
                aria-label="How to use Website Widget"
              >
                <Info size={14} />
              </button>
              <div className="hidden group-hover:block absolute left-8 top-0 z-20 w-[340px] rounded-md border border-slate-200 bg-white p-3 shadow-lg">
                <p className="text-xs font-semibold text-slate-700 mb-1">
                  How to use
                </p>
                <ol className="list-decimal ml-4 space-y-1 text-xs text-slate-600">
                  <li>Set your WhatsApp destination number.</li>
                  <li>Add allowed origins (website domains).</li>
                  <li>Copy and use widget key in marketing env.</li>
                  <li>Keep widget active to accept lead submissions.</li>
                  <li>On submit: lead is saved, then WhatsApp opens.</li>
                </ol>
              </div>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            Public website lead capture + WhatsApp handoff (provider: META_CLOUD).
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={!!state.isActive}
            onChange={onChange("isActive")}
          />
          Widget active
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading widget config...</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Public Widget Key
              </label>
              <div className="flex items-center gap-2">
                <input
                  value={state.widgetKey || ""}
                  readOnly
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-slate-50"
                />
                <button
                  type="button"
                  onClick={() => copyText(state.widgetKey)}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-2 text-sm hover:bg-slate-50"
                >
                  <Copy size={14} />
                  Copy
                </button>
                <button
                  type="button"
                  onClick={onRotateKey}
                  disabled={rotating}
                  className="inline-flex items-center gap-1 rounded-md border border-amber-300 px-2 py-2 text-sm text-amber-800 hover:bg-amber-50 disabled:opacity-60"
                >
                  <RefreshCcw size={14} />
                  Rotate
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                WhatsApp Destination Number
              </label>
              <input
                value={state.whatsAppNumber || ""}
                onChange={onChange("whatsAppNumber")}
                placeholder="e.g. 918826165727"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Title
              </label>
              <input
                value={state.title || ""}
                onChange={onChange("title")}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Subtitle
              </label>
              <input
                value={state.subtitle || ""}
                onChange={onChange("subtitle")}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Welcome Text
            </label>
            <input
              value={state.welcomeText || ""}
              onChange={onChange("welcomeText")}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Intent Chips (comma-separated)
              </label>
              <input
                value={chipsText}
                onChange={e => setChipsText(e.target.value)}
                placeholder="Pricing, Book demo, Help, More"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Header Color
                </label>
                <input
                  value={state.headerBgColor || ""}
                  onChange={onChange("headerBgColor")}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Button Color
                </label>
                <input
                  value={state.buttonBgColor || ""}
                  onChange={onChange("buttonBgColor")}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!state.requireName}
                onChange={onChange("requireName")}
              />
              Require Name
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!state.requireEmailOrPhone}
                onChange={onChange("requireEmailOrPhone")}
              />
              Require Email or Phone
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Allowed Origins (one per line)
            </label>
            <textarea
              value={allowedOriginsText}
              onChange={e => setAllowedOriginsText(e.target.value)}
              rows={3}
              placeholder={"https://xplorebyte.com\nhttps://www.xplorebyte.com"}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Embed Snippet (Phase 2 ready)
            </label>
            <div className="relative">
              <pre className="bg-slate-900 text-slate-100 text-xs rounded-md p-3 overflow-x-auto">
                {embedSnippet || "Rotate/generate widget key to get embed snippet."}
              </pre>
              {embedSnippet ? (
                <button
                  type="button"
                  onClick={() => copyText(embedSnippet)}
                  className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-100 hover:bg-slate-700"
                >
                  <Copy size={12} />
                  Copy
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center rounded-md bg-emerald-600 px-4 py-2 text-sm text-white disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Widget Config"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
